import React, { useEffect, useState } from 'react';
import { ICustomerNFItem } from '../../../../../../store/ducks/download-area/types';
import { useDispatch } from 'react-redux';
import { sendTagging } from '../../../../../../store/ducks/tagging/actions';
import { Button, Icon } from '@gerdau/hefesto';
import { InformationPanel } from '@gerdau/hefesto-mais-web/InformationPanel';
import { Variants } from '@gerdau/hefesto/_shared/types';
import {
  loadNFFile,
  downloadNFFile,
  loadNFXMLFile,
  downloadNFXMLFile
} from '../../../../../../store/ducks/download-area/action';
import { IDownloadNFEResponse } from '../../../../../../store/ducks/long-steel-orders/types';
import { SpinnerIcon } from '../../../../../../components/Commons/SpinnerIcon/styles';

interface INFDownloadButton {
  item: ICustomerNFItem;
}
interface INFDownloadDocumentsOptions {
  text: string;
  handleOnClick: () => void;
  icon?: JSX.Element;
  variant?: Extract<Variants, 'danger' | 'primary'>;
  disabled: boolean;
}

const NFDownloadButton: React.FC<INFDownloadButton> = ({ item }) => {
  const dispatch = useDispatch();
  const [nfAlreadyDownloaded, setNfAlreadyDownloaded] = useState(false);
  const [xmlAlreadyDownloaded, setXmlAlreadyDownloaded] = useState(false);

  const downloadFromUseEffect = () => {
    dispatch(downloadNFFile(item));
    downloadFile(item.nfState?.downloadNFEResponse as IDownloadNFEResponse);
  };

  const downloadXMLFromUseEffect = () => {
    dispatch(downloadNFXMLFile(item));
    if (item.nfXmlState?.downloadNFEResponse) {
      return downloadXMLFile(
        item.nfXmlState.downloadNFEResponse.FILE,
        `NF-${item.invoiceNumber}.xml`
      );
    }
  };

  useEffect(() => {
    if (
      !item?.nfState?.alreadyDownloaded &&
      item?.nfState?.downloadNFEResponse &&
      !nfAlreadyDownloaded
    ) {
      downloadFromUseEffect();
      setNfAlreadyDownloaded(true);
    }
  });

  useEffect(() => {
    if (
      !item?.nfXmlState?.alreadyDownloaded &&
      item?.nfXmlState?.downloadNFEResponse &&
      !xmlAlreadyDownloaded
    ) {
      downloadXMLFromUseEffect();
      setXmlAlreadyDownloaded(true);
    }
  });

  const downloadNFE = () => {
    dispatch(
      sendTagging({
        event: 'click',
        itemDescription: 'buscarDocumentos_notaFiscal_baixar_baixarPdf',
        itemName: 'button:baixar_pdf',
        screenName: 'LG:BuscarDocumentos:NotaFiscal'
      })
    );

    if (item.nfState?.downloadNFEResponse) {
      return downloadFile(item.nfState.downloadNFEResponse);
    }

    dispatch(loadNFFile(item));
  };

  const downloadFile = (downloadNFEResponse: IDownloadNFEResponse) => {
    const dataurl = 'data:text/plain;base64,' + downloadNFEResponse.FILE;
    const element = document.createElement('a');
    element.setAttribute('href', dataurl);
    element.setAttribute('download', `NF-${item.invoiceNumber}.pdf`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const downloadNFEXML = () => {
    dispatch(
      sendTagging({
        event: 'click',
        itemDescription: 'buscarDocumentos_notaFiscal_baixar_baixarXml',
        itemName: 'button:baixar_xml',
        screenName: 'LG:BuscarDocumentos:NotaFiscal'
      })
    );
    if (item.nfXmlState?.downloadNFEResponse) {
      return downloadXMLFile(
        item.nfXmlState.downloadNFEResponse.FILE,
        `NF-${item.invoiceNumber}.xml`
      );
    }
    dispatch(loadNFXMLFile(item));
  };

  const downloadXMLFile = (downloadNFEResponse: string, fileName: string) => {
    const byteCharacters = atob(downloadNFEResponse);
    const byteNumbers = byteCharacters
      .split('')
      .map((_, idx) => byteCharacters.charCodeAt(idx));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/xml' });

    const link = document.createElement('a');
    link.download = fileName;
    link.href = window.URL.createObjectURL(blob);
    link.click();
  };

  const optionsDocuments: INFDownloadDocumentsOptions[] = [
    {
      text: item?.nfState?.loading ? 'Baixando PDF' : 'Baixar PDF',
      handleOnClick: downloadNFE,
      variant: 'primary',
      disabled: Boolean(item?.nfState?.loading),
      icon: item?.nfState?.loading ? (
        <SpinnerIcon style={{ marginRight: '0.125rem' }} />
      ) : (
        <Icon option="Download" />
      )
    },
    {
      text: item?.nfXmlState?.loading ? 'Baixando XML' : 'Baixar XML',
      handleOnClick: downloadNFEXML,
      variant: 'primary',
      disabled: Boolean(item?.nfXmlState?.loading),
      icon: item?.nfXmlState?.loading ? (
        <SpinnerIcon style={{ marginRight: '0.125rem' }} />
      ) : (
        <Icon option="Download" />
      )
    }
  ];

  return (
    <InformationPanel
      trigger={
        <div>
          <Button
            name="documents"
            variant="tertiary"
            icon={
              item?.nfState?.loading || item?.nfXmlState?.loading ? (
                <SpinnerIcon />
              ) : (
                <Icon option="MoreVertical" />
              )
            }
            disabled={item?.nfState?.loading || item?.nfXmlState?.loading}
          >
            {item?.nfState?.loading || item?.nfXmlState?.loading
              ? 'Baixando'
              : 'Baixar'}
          </Button>
        </div>
      }
      position="bottom right"
      title="Documentos"
      panelOptions={optionsDocuments}
    />
  );
};

export default NFDownloadButton;
